import * as THREE from "three";
import { InstanceBatch } from "./instance_batch.js";
import { Vfx3DGlobalAtlas } from "../textures/vfx_3d_global_atlas.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { AttributeSize } from "../types/core_types.js";
import { randomRange } from "../../../common/common_funcs.js";

export const PhysicsParticleLayout = {
  aPosStartTime:  AttributeSize.Vec4,       // x, y, z, start_time
  aVelDur:        AttributeSize.Vec4,       // vx, vy, vz, duration
  aPhysicsParams: AttributeSize.Vec4,       // gravity, scale, floor_y, restitution
  aFadeParams:    AttributeSize.Vec4,       // fade_start, shrink_start, flash_intensity, initial_dissolve
  aUvBounds:      AttributeSize.Vec4,       // min_x, min_y, max_x, max_y (surface texture or shard texture)
  aRotation:      AttributeSize.Vec4,       // initial_angle, angular_speed, noise_scale, unused
  aUvShape:       AttributeSize.Vec4,       // min_x, min_y, max_x, max_y for shape mask (or -1.0 if solid shard)
  aUvNoise:       AttributeSize.Vec4,       // min_x, min_y, max_x, max_y for noise erosion texture
} as const;



export class VfxPhysicsParticleBatch extends InstanceBatch<typeof PhysicsParticleLayout>
{
  public static readonly MAX_INSTANCES : number = 1000;
  public static readonly RENDER_ORDER  : number = 21;
  public static readonly DEFAULT_GRAVITY: number = 14.0;

  public static readonly FLUID_PARTICLE_IDS: readonly ResourceID[] = [
    ResourceID.FluidParticle1,
    ResourceID.FluidParticle2,
    ResourceID.FluidParticle3,
  ];

  constructor()
  {
    const base_geometry = new THREE.PlaneGeometry( 1, 1 );

    let atlas_texture: THREE.Texture | null = null;
    try { atlas_texture = Vfx3DGlobalAtlas.getTexture(); } catch ( _e ) {}

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uTexture: { value: atlas_texture },
        uTime:    { value: 0.0 },
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosStartTime;
        attribute vec4 aVelDur;
        attribute vec4 aPhysicsParams;
        attribute vec4 aFadeParams;
        attribute vec4 aUvBounds;
        attribute vec4 aRotation;
        attribute vec4 aUvShape;
        attribute vec4 aUvNoise;

        varying vec2 vUv;
        varying vec2 vUvShape;
        varying vec4 vUvNoise;
        varying vec2 vThresholdParams;
        varying vec4 vColor;
        varying float vUseShape;
        varying vec2 vLocalUv;
        varying float vNoiseScale;

        const float POP_DURATION = 0.1;

        void main()
        {
          vUv = mix( aUvBounds.xy, aUvBounds.zw, uv );

          float useShape = aUvShape.x >= 0.0 ? 1.0 : 0.0;
          vUseShape = useShape;
          if ( useShape > 0.5 )
          {
            vUvShape = mix( aUvShape.xy, aUvShape.zw, uv );
            vUvNoise = aUvNoise;
            vNoiseScale = aRotation.z > 0.0 ? aRotation.z : 1.0;
            vLocalUv = uv;
          }

          float start_time = aPosStartTime.w;
          float duration = aVelDur.w;
          float age = max( 0.0, uTime - start_time );
          float progress = clamp( age / duration, 0.0, 1.0 );

          float scale_factor = 1.0;
          float alpha_factor = 1.0;

          if ( useShape > 0.5 )
          {
            // Fluid splash pop scale & dissolve thresholds
            float pop = clamp( age / POP_DURATION, 0.0, 1.0 );
            scale_factor = 0.6 + 0.4 * pop;

            float dissolve_start = aFadeParams.x;
            float dissolve_softness = max( aFadeParams.y, 0.001 );
            float initDissolve = clamp( aFadeParams.w, 0.0, 1.0 );
            float advanceProgress = clamp( ( progress - dissolve_start ) / max( 1.0 - dissolve_start, 0.001 ), 0.0, 1.0 );
            float currentDissolve = mix( initDissolve, 1.0, advanceProgress );
            float threshold = currentDissolve * ( 1.0 + dissolve_softness );
            vThresholdParams = vec2( threshold - dissolve_softness, threshold );
          }
          else
          {
            // Shard shrink calculation: if shrink_start < 1.0, shrink down to 0 after shrink_start
            float shrink_start = aFadeParams.y;
            if ( shrink_start < 1.0 && progress > shrink_start )
            {
              scale_factor = 1.0 - clamp( ( progress - shrink_start ) / max( 1.0 - shrink_start, 0.001 ), 0.0, 1.0 );
            }

            // Shard alpha fade calculation: if fade_start < 1.0, fade down to 0 after fade_start
            float fade_start = aFadeParams.x;
            if ( fade_start < 1.0 && progress > fade_start )
            {
              alpha_factor = 1.0 - clamp( ( progress - fade_start ) / max( 1.0 - fade_start, 0.001 ), 0.0, 1.0 );
            }
          }

          float current_scale = aPhysicsParams.y * scale_factor;

          // Rotation & Specular flash calculation (for shiny shards like glass or crystals)
          float angle = aRotation.x + aRotation.y * age;
          float flash_intensity = aFadeParams.z;
          float flash = ( flash_intensity > 0.0 && useShape < 0.5 ) ? pow( abs( sin( angle * 2.0 ) ), 10.0 ) * flash_intensity : 0.0;
          float boost = 1.0 + flash;

          vColor = vec4( vec3( boost ), alpha_factor );

          // Physics trajectory with floor bounce
          float gravity = aPhysicsParams.x;
          float floor_y = aPhysicsParams.z;
          float restitution = aPhysicsParams.w;

          vec3 computed_pos = aPosStartTime.xyz + aVelDur.xyz * age + vec3( 0.0, -0.5 * gravity * age * age, 0.0 );

          if ( floor_y > -900.0 && gravity > 0.0 )
          {
            float D = aVelDur.y * aVelDur.y + 2.0 * gravity * ( aPosStartTime.y - floor_y );
            float t_hit = ( aVelDur.y + sqrt( max( 0.0, D ) ) ) / gravity;

            if ( age > t_hit && t_hit > 0.0 )
            {
              if ( restitution > 0.0 )
              {
                float time_after = age - t_hit;
                float v_hit = aVelDur.y - gravity * t_hit;
                float v_bounce = -v_hit * restitution;
                float bounce_y = floor_y + v_bounce * time_after - 0.5 * gravity * time_after * time_after;
                computed_pos.y = max( floor_y, bounce_y );
              }
              else
              {
                computed_pos.y = floor_y;
              }
            }
            else
            {
              computed_pos.y = max( computed_pos.y, floor_y );
            }
          }

          float c = cos( angle );
          float s = sin( angle );
          mat2 rot2d = mat2( c, -s, s, c );
          vec2 rot_pos = rot2d * position.xy;

          vec4 mvPosition = modelViewMatrix * vec4( computed_pos, 1.0 );
          mvPosition.xy += rot_pos * current_scale;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        varying vec2 vUv;
        varying vec2 vUvShape;
        varying vec4 vUvNoise;
        varying vec2 vThresholdParams;
        varying vec4 vColor;
        varying float vUseShape;
        varying vec2 vLocalUv;
        varying float vNoiseScale;

        void main()
        {
          vec4 sampled = texture2D( uTexture, vUv );

          if ( vUseShape > 0.5 )
          {
            vec4 shapeTex = texture2D( uTexture, vUvShape );
            float shapeMask = shapeTex.a > 0.001 ? ( shapeTex.r * shapeTex.a ) : shapeTex.r;

            vec2 noiseUv = fract( vLocalUv * vNoiseScale );
            vec2 atlasNoiseUv = mix( vUvNoise.xy, vUvNoise.zw, noiseUv );
            vec4 erosionTex = texture2D( uTexture, atlasNoiseUv );

            float dissolveAlpha = smoothstep( vThresholdParams.x, vThresholdParams.y, erosionTex.r );

            float finalAlpha = shapeMask * sampled.a * dissolveAlpha;
            gl_FragColor = vec4( sampled.rgb, finalAlpha );
          }
          else
          {
            vec4 final_color = sampled * vec4( vColor.rgb, 1.0 );
            final_color.a *= vColor.a;
            gl_FragColor = final_color;
          }
        }
      `,
      transparent: true,
      depthWrite:  false,
      depthTest:   true,
      blending:    THREE.NormalBlending,
      side:        THREE.FrontSide,
    } );

    super( { max_instances: VfxPhysicsParticleBatch.MAX_INSTANCES, layout: PhysicsParticleLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxPhysicsParticleBatch.RENDER_ORDER;
  }

  public spawnShard(
    res_id: ResourceID,
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    gravity: number,
    scale: number,
    initial_angle: number,
    angular_speed: number,
    start_time: number,
    duration: number,
    floor_y: number = -999.0,
    fade_start: number = 0.6,
    shrink_start: number = 1.0,
    flash_intensity: number = 0.0,
    restitution: number = 0.25
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !mat.uniforms.uTexture.value )
      try { mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture(); } catch ( _e ) {}

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const bounds = Vfx3DGlobalAtlas.getUVRect( res_id );

    const pos_start_attr   = this.geometry.getAttribute( "aPosStartTime" ).array as Float32Array;
    const vel_dur_attr     = this.geometry.getAttribute( "aVelDur" ).array as Float32Array;
    const physics_attr     = this.geometry.getAttribute( "aPhysicsParams" ).array as Float32Array;
    const fade_attr        = this.geometry.getAttribute( "aFadeParams" ).array as Float32Array;
    const uv_bounds_attr   = this.geometry.getAttribute( "aUvBounds" ).array as Float32Array;
    const rotation_attr    = this.geometry.getAttribute( "aRotation" ).array as Float32Array;
    const uv_shape_attr    = this.geometry.getAttribute( "aUvShape" ).array as Float32Array;

    const uv_noise_attr  = this.geometry.getAttribute( "aUvNoise" ).array as Float32Array;

    const o4 = idx * 4;

    pos_start_attr[ o4 ]     = start_pos.x;
    pos_start_attr[ o4 + 1 ] = start_pos.y;
    pos_start_attr[ o4 + 2 ] = start_pos.z;
    pos_start_attr[ o4 + 3 ] = start_time;

    vel_dur_attr[ o4 ]     = velocity.x;
    vel_dur_attr[ o4 + 1 ] = velocity.y;
    vel_dur_attr[ o4 + 2 ] = velocity.z;
    vel_dur_attr[ o4 + 3 ] = duration;

    physics_attr[ o4 ]     = gravity;
    physics_attr[ o4 + 1 ] = scale;
    physics_attr[ o4 + 2 ] = floor_y;
    physics_attr[ o4 + 3 ] = restitution;

    fade_attr[ o4 ]     = fade_start;
    fade_attr[ o4 + 1 ] = shrink_start;
    fade_attr[ o4 + 2 ] = flash_intensity;
    fade_attr[ o4 + 3 ] = 0.0;

    uv_bounds_attr[ o4 ]     = bounds.min_x;
    uv_bounds_attr[ o4 + 1 ] = bounds.min_y;
    uv_bounds_attr[ o4 + 2 ] = bounds.max_x;
    uv_bounds_attr[ o4 + 3 ] = bounds.max_y;

    // Solid shard marker: negative shape bounds indicate no fluid shape mask
    uv_shape_attr[ o4 ]     = -1.0;
    uv_shape_attr[ o4 + 1 ] = -1.0;
    uv_shape_attr[ o4 + 2 ] = -1.0;
    uv_shape_attr[ o4 + 3 ] = -1.0;

    uv_noise_attr[ o4 ]     = 0.0;
    uv_noise_attr[ o4 + 1 ] = 0.0;
    uv_noise_attr[ o4 + 2 ] = 0.0;
    uv_noise_attr[ o4 + 3 ] = 0.0;

    // Ensure rotation direction matches horizontal motion direction (rolling forward along the ground)
    let final_angular_speed = angular_speed;
    if ( Math.abs( velocity.x ) > 0.05 )
    {
      const roll_dir = velocity.x > 0 ? 1 : -1;
      final_angular_speed = roll_dir * Math.abs( angular_speed );
    }

    rotation_attr[ o4 ]     = initial_angle;
    rotation_attr[ o4 + 1 ] = final_angular_speed;
    rotation_attr[ o4 + 2 ] = 1.0;
    rotation_attr[ o4 + 3 ] = 0.0;

    this.geometry.getAttribute( "aPosStartTime" ).needsUpdate   = true;
    this.geometry.getAttribute( "aVelDur" ).needsUpdate         = true;
    this.geometry.getAttribute( "aPhysicsParams" ).needsUpdate   = true;
    this.geometry.getAttribute( "aFadeParams" ).needsUpdate      = true;
    this.geometry.getAttribute( "aUvBounds" ).needsUpdate       = true;
    this.geometry.getAttribute( "aRotation" ).needsUpdate       = true;
    this.geometry.getAttribute( "aUvShape" ).needsUpdate        = true;
    this.geometry.getAttribute( "aUvNoise" ).needsUpdate        = true;

    return this.getStableId( idx );
  }

  public spawnParticle(
    surface_res_id: ResourceID,
    noise_res_id: ResourceID,
    start_pos: THREE.Vector3,
    velocity: THREE.Vector3,
    gravity: number = VfxPhysicsParticleBatch.DEFAULT_GRAVITY,
    scale: number = 0.6,
    start_time: number = 0.0,
    duration: number = 0.8,
    initial_dissolve: number = 0.0,
    noise_scale: number = 1.0,
    rotation?: number,
    shape_sub_index?: number,
    shape_res_id?: ResourceID,
    dissolve_start: number = 0.1,
    dissolve_softness: number = 0.1
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !mat.uniforms.uTexture.value )
      try { mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture(); } catch ( _e ) {}

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const surface_uv = Vfx3DGlobalAtlas.getUVRect( surface_res_id ) ?? { min_x: 0, min_y: 0, max_x: 1, max_y: 1 };
    const noise_uv   = Vfx3DGlobalAtlas.getUVRect( noise_res_id ) ?? { min_x: 0, min_y: 0, max_x: 1, max_y: 1 };

    const shape_id = shape_res_id ?? (
      ( shape_sub_index !== undefined && shape_sub_index >= 0 )
        ? VfxPhysicsParticleBatch.FLUID_PARTICLE_IDS[ shape_sub_index % VfxPhysicsParticleBatch.FLUID_PARTICLE_IDS.length ]
        : VfxPhysicsParticleBatch.FLUID_PARTICLE_IDS[ Math.floor( Math.random() * VfxPhysicsParticleBatch.FLUID_PARTICLE_IDS.length ) ]
    );
    const shape_uv = Vfx3DGlobalAtlas.getUVRect( shape_id ) ?? { min_x: 0, min_y: 0, max_x: 1, max_y: 1 };

    const pos_start_attr = this.geometry.getAttribute( "aPosStartTime" ).array as Float32Array;
    const vel_dur_attr   = this.geometry.getAttribute( "aVelDur" ).array as Float32Array;
    const physics_attr   = this.geometry.getAttribute( "aPhysicsParams" ).array as Float32Array;
    const fade_attr      = this.geometry.getAttribute( "aFadeParams" ).array as Float32Array;
    const uv_bounds_attr = this.geometry.getAttribute( "aUvBounds" ).array as Float32Array;
    const rotation_attr  = this.geometry.getAttribute( "aRotation" ).array as Float32Array;
    const uv_shape_attr  = this.geometry.getAttribute( "aUvShape" ).array as Float32Array;
    const uv_noise_attr  = this.geometry.getAttribute( "aUvNoise" ).array as Float32Array;

    const o4 = idx * 4;

    pos_start_attr[ o4 ]     = start_pos.x;
    pos_start_attr[ o4 + 1 ] = start_pos.y;
    pos_start_attr[ o4 + 2 ] = start_pos.z;
    pos_start_attr[ o4 + 3 ] = start_time;

    vel_dur_attr[ o4 ]     = velocity.x;
    vel_dur_attr[ o4 + 1 ] = velocity.y;
    vel_dur_attr[ o4 + 2 ] = velocity.z;
    vel_dur_attr[ o4 + 3 ] = duration;

    physics_attr[ o4 ]     = gravity;
    physics_attr[ o4 + 1 ] = scale;
    physics_attr[ o4 + 2 ] = 0.0; // Floor Y
    physics_attr[ o4 + 3 ] = 0.0; // Restitution (liquid stops at floor)

    fade_attr[ o4 ]     = dissolve_start;
    fade_attr[ o4 + 1 ] = dissolve_softness;
    fade_attr[ o4 + 2 ] = 0.0;
    fade_attr[ o4 + 3 ] = initial_dissolve ?? 0.0;

    uv_bounds_attr[ o4 ]     = surface_uv.min_x;
    uv_bounds_attr[ o4 + 1 ] = surface_uv.min_y;
    uv_bounds_attr[ o4 + 2 ] = surface_uv.max_x;
    uv_bounds_attr[ o4 + 3 ] = surface_uv.max_y;

    uv_shape_attr[ o4 ]     = shape_uv.min_x;
    uv_shape_attr[ o4 + 1 ] = shape_uv.min_y;
    uv_shape_attr[ o4 + 2 ] = shape_uv.max_x;
    uv_shape_attr[ o4 + 3 ] = shape_uv.max_y;

    uv_noise_attr[ o4 ]     = noise_uv.min_x;
    uv_noise_attr[ o4 + 1 ] = noise_uv.min_y;
    uv_noise_attr[ o4 + 2 ] = noise_uv.max_x;
    uv_noise_attr[ o4 + 3 ] = noise_uv.max_y;

    rotation_attr[ o4 ]     = rotation !== undefined ? rotation : randomRange( 0, Math.PI * 2 );
    rotation_attr[ o4 + 1 ] = 0.0;
    rotation_attr[ o4 + 2 ] = noise_scale;
    rotation_attr[ o4 + 3 ] = 0.0;

    this.geometry.getAttribute( "aPosStartTime" ).needsUpdate   = true;
    this.geometry.getAttribute( "aVelDur" ).needsUpdate         = true;
    this.geometry.getAttribute( "aPhysicsParams" ).needsUpdate   = true;
    this.geometry.getAttribute( "aFadeParams" ).needsUpdate      = true;
    this.geometry.getAttribute( "aUvBounds" ).needsUpdate       = true;
    this.geometry.getAttribute( "aRotation" ).needsUpdate       = true;
    this.geometry.getAttribute( "aUvShape" ).needsUpdate        = true;
    this.geometry.getAttribute( "aUvNoise" ).needsUpdate        = true;

    return this.getStableId( idx );
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }

  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override onRender(): void
  {
    const atlas_tex = Vfx3DGlobalAtlas.getTexture();
    if ( atlas_tex )
    {
      const mat = this.material as THREE.ShaderMaterial;
      if ( mat.uniforms.uTexture.value !== atlas_tex )
      {
        mat.uniforms.uTexture.value = atlas_tex;
        mat.uniformsNeedUpdate = true;
      }
      if ( mat.uniforms.uNoiseUv )
      {
        const n_rect = Vfx3DGlobalAtlas.getUVRect( ResourceID.PerlinNoiseTexture );
        if ( n_rect )
        {
          ( mat.uniforms.uNoiseUv.value as THREE.Vector4 ).set( n_rect.min_x, n_rect.min_y, n_rect.max_x, n_rect.max_y );
        }
      }
    }
  }
}
